import { LanguageSelector } from "./LanguageSelector";
import { Wordmark } from "./Wordmark";
import { useI18n, type LocaleCode } from "@/i18n";
import { business } from "@/config/business";
import {
  publishedServicePages,
  SERVICE_CATEGORY_LABELS,
  SERVICE_CATEGORY_ORDER,
  type ServiceCategoryId,
} from "@/config/services";
import { confirmedServiceAreas } from "@/config/service-areas";
import { localizePath } from "@/i18n";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { localizeServiceArea, localizeServicePage } from "@/i18n/content";
import { getMarketingCopy } from "@/i18n/marketing-copy";

const FOOTER_CATEGORY_COPY: Partial<
  Record<LocaleCode, Partial<Record<ServiceCategoryId, string>>>
> = {
  "fa-ir": {
    "emergency-repairs": "خدمات فوری و تعمیرات",
    replacement: "تعویض و اجرای کامل سقف",
    maintenance: "بازرسی و نگهداری",
    "gutters-exterior": "ناودان و اجزای بیرونی سقف",
  },
  "ar-sa": {
    "emergency-repairs": "الطوارئ والإصلاحات",
    replacement: "استبدال السقف وأنظمته الكاملة",
    maintenance: "الفحص والصيانة",
    "gutters-exterior": "المزاريب ومكونات حواف السقف",
  },
};

const FOOTER_HUB_COPY: Record<LocaleCode, readonly [string, string, string, string]> = {
  "en-ca": ["Residential", "Commercial", "Strata", "Roof systems"],
  "zh-cn": ["住宅屋顶", "商业屋顶", "共管物业", "屋顶系统"],
  "zh-tw": ["住宅屋頂", "商業屋頂", "共管物業", "屋頂系統"],
  "pa-in": ["ਰਿਹਾਇਸ਼ੀ", "ਵਪਾਰਕ", "ਸਟ੍ਰਾਟਾ", "ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ"],
  "ko-kr": ["주택", "상업용", "공동주택", "지붕 시스템"],
  "fa-ir": ["سقف مسکونی", "سقف تجاری", "مجتمع‌های استراتا", "سیستم‌های سقف"],
  "ar-sa": ["الأسقف السكنية", "الأسقف التجارية", "مجمعات الستراتا", "أنظمة الأسقف"],
};

const FOOTER_HUB_PATHS = [
  "/residential-roofing",
  "/commercial-roofing",
  "/strata-roofing",
  "/roof-systems",
] as const;

const FOOTER_COMPANY_PATHS = [
  "/emergency-roofing",
  "/before-after",
  "/about",
  "/roof-estimate",
  "/service-areas/british-columbia",
] as const;

const FOOTER_COMPANY_COPY: Record<LocaleCode, readonly [string, string, string, string, string]> = {
  "en-ca": ["Emergency", "Projects", "About", "Get Estimate", "British Columbia"],
  "zh-cn": ["紧急服务", "项目案例", "关于我们", "获取估价", "卑诗省"],
  "zh-tw": ["緊急服務", "工程案例", "關於我們", "取得估價", "卑詩省"],
  "pa-in": ["ਐਮਰਜੈਂਸੀ", "ਪ੍ਰੋਜੈਕਟ", "ਸਾਡੇ ਬਾਰੇ", "ਅੰਦਾਜ਼ਾ ਲਵੋ", "ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ"],
  "ko-kr": ["긴급 서비스", "시공 사례", "회사 소개", "견적 받기", "브리티시컬럼비아"],
  "fa-ir": ["خدمات اضطراری", "پروژه‌ها", "دربارهٔ ما", "دریافت برآورد", "بریتیش کلمبیا"],
  "ar-sa": ["خدمات الطوارئ", "المشاريع", "من نحن", "اطلب تقديراً", "بريتيش كولومبيا"],
};

export function Footer() {
  const { t, locale } = useI18n();
  const path = (href: string) => localizePath(href, locale);
  const marketing = getMarketingCopy(locale);
  const contact = marketing?.contactDetails;
  const serviceGroups = SERVICE_CATEGORY_ORDER.map((category) => ({
    category,
    services: publishedServicePages
      .filter((service) => service.category === category)
      .map((service) => localizeServicePage(service, locale)),
  })).filter((group) => group.services.length > 0);
  const areas = confirmedServiceAreas.map((area) => localizeServiceArea(area, locale));

  return (
    <footer className="border-t border-accent/20 bg-primary pb-24 text-primary-foreground lg:pb-0">
      <div className="container-page grid gap-12 py-14 lg:grid-cols-[0.72fr_1.28fr]">
        <div className="grid content-start gap-10 sm:grid-cols-2 lg:grid-cols-1">
          <div>
            <Wordmark onDark />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-primary-foreground/65">
              {t.footer.disclaimer}
            </p>
            <nav aria-label={t.footer.servicesTitle} className="mt-6">
              <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
                <li>
                  <a
                    href={path("/services")}
                    className="font-semibold text-accent underline-offset-4 hover:underline"
                  >
                    {t.nav.roofTypes}
                  </a>
                </li>
                {FOOTER_HUB_PATHS.map((hubPath, index) => (
                  <li key={hubPath}>
                    <a
                      href={path(hubPath)}
                      className="text-primary-foreground/70 underline-offset-4 hover:text-accent hover:underline"
                    >
                      {FOOTER_HUB_COPY[locale][index]}
                    </a>
                  </li>
                ))}
                {FOOTER_COMPANY_PATHS.map((companyPath, index) => (
                  <li key={companyPath}>
                    <a
                      href={path(companyPath)}
                      className="text-primary-foreground/70 underline-offset-4 hover:text-accent hover:underline"
                    >
                      {FOOTER_COMPANY_COPY[locale][index]}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div>
            <h2 className="text-[0.7rem] tracking-[0.16em] uppercase text-accent">
              {t.footer.contactTitle}
            </h2>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a
                  href={path("/faq")}
                  className="text-primary-foreground/78 underline-offset-4 hover:text-accent hover:underline"
                >
                  {t.nav.faq}
                </a>
              </li>
              <li>
                <a
                  href={business.phoneHref}
                  className="text-primary-foreground/85 underline-offset-4 hover:text-accent hover:underline"
                  onClick={() =>
                    trackAnalyticsEvent({ event: "phone_click", link_location: "site_footer" })
                  }
                >
                  {business.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${business.email}`}
                  className="text-primary-foreground/85 underline-offset-4 hover:text-accent hover:underline"
                >
                  {business.email}
                </a>
              </li>
              <li className="text-primary-foreground/60">
                {contact?.serviceArea ?? "Service region"}:{" "}
                {contact?.region ?? "Metro Vancouver & Lower Mainland"}
              </li>
              <li>
                <a
                  href={business.parentUrl}
                  rel="noopener"
                  className="text-primary-foreground/60 underline-offset-4 hover:text-accent hover:underline"
                >
                  {locale === "fa-ir"
                    ? "از شرکت‌های گروه رولین"
                    : locale === "ar-sa"
                      ? "إحدى شركات مجموعة رولين"
                      : business.parent}
                </a>
              </li>
            </ul>
            <div className="mt-4">
              <LanguageSelector onDark className="-ml-3" />
            </div>
          </div>
        </div>

        <div className="space-y-10">
          <nav aria-label={t.footer.servicesTitle}>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-primary-foreground/12 pb-4">
              <h2 className="text-[0.7rem] tracking-[0.16em] uppercase text-accent">
                {t.footer.servicesTitle}
              </h2>
              <span className="text-xs tabular-nums text-primary-foreground/45">
                {new Intl.NumberFormat(locale).format(publishedServicePages.length)}
              </span>
            </div>
            <div className="mt-5 grid gap-7 sm:grid-cols-2">
              {serviceGroups.map(({ category, services }) => (
                <section key={category} aria-labelledby={`footer-${category}`}>
                  <h3
                    id={`footer-${category}`}
                    className="text-xs font-semibold tracking-[0.08em] text-primary-foreground/90 uppercase"
                  >
                    {FOOTER_CATEGORY_COPY[locale]?.[category] ??
                      marketing?.serviceCategories[category] ??
                      SERVICE_CATEGORY_LABELS[category]}
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {services.map((service) => (
                      <li key={service.slug}>
                        <a
                          href={path(service.path)}
                          className="text-primary-foreground/68 underline-offset-4 hover:text-accent hover:underline"
                        >
                          {service.shortTitle}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </nav>

          <nav aria-label={t.footer.areasTitle}>
            <h2 className="text-[0.7rem] tracking-[0.16em] uppercase text-accent">
              {t.footer.areasTitle}
            </h2>
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-3">
              <li>
                <a
                  href={path("/service-areas")}
                  className="font-semibold text-accent underline-offset-4 hover:underline"
                >
                  {t.nav.areas}
                </a>
              </li>
              {areas.map((area) => (
                <li key={area.slug}>
                  <a
                    href={path(`/service-areas/${area.slug}`)}
                    className="text-primary-foreground/68 underline-offset-4 hover:text-accent hover:underline"
                  >
                    {area.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      <div className="border-t border-primary-foreground/12">
        <div className="container-page flex flex-col gap-3 py-6 text-xs text-primary-foreground/55 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {business.name}. {t.footer.rights}
          </p>
          <div className="flex gap-5">
            <a
              href={path("/privacy")}
              className="underline-offset-4 hover:text-accent hover:underline"
            >
              {t.footer.privacy}
            </a>
            <a
              href={path("/terms")}
              className="underline-offset-4 hover:text-accent hover:underline"
            >
              {t.footer.terms}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
